import {
    Box,
    Button,
    Divider,
    Event,
    Heading,
    TextBlock,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import React from "react";
import { useTranslation } from "react-i18next";

import style from "./risk-resolving-step.scss";
import { RiskDistributionCard } from "components/asset-management-system/custom-views/tabs/risk/cards/risk-distribution-card/RiskDistributionCard";
import {
    calculateTotalAssetsCount,
    toRiskSummary,
} from "components/asset-management-system/custom-views/tabs/value/steps/risk-resolving-step/riskResolvingSummary";
import { SmartGradingBanner } from "components/asset-management-system/custom-views/tabs/value/steps/value-step-card/smart-grading-banner/SmartGradingBanner";
import { ValueStepCard } from "components/asset-management-system/custom-views/tabs/value/steps/value-step-card/ValueStepCard";
import { GradingStep, ResolveRiskSummary } from "services/asset-management/AssetGradingService";

import testIds from "testIds.json";

interface Props {
    isSmartGradingEnabled: boolean;
    handleSmartGradingChange: (value: boolean) => void;
    handleGradingStepSave: (step: GradingStep, enabled: boolean) => void;
    handleResolveRisks: () => void;
    handleContinueGrading: () => void;
    isLaunchingGrading: boolean;
    isSmartGradingLicenseAvailable: boolean;
    allAssetsEligibleForGrading: boolean;
    someAssetsEligibleForGrading: boolean;
    data: ResolveRiskSummary;
    currentlyCompletedStep: number;
    isGradingBannerToggleDisabled?: boolean;
}

export const RiskResolvingStep = (props: Props) => {
    const { t } = useTranslation();

    const eligibleAssetsCount = props.data.assetsExcludingLockedAndQuarantinedCount;
    const riskSummary = toRiskSummary(props.data);

    const riskDistributionCardContent = [
        {
            variant: "var(--app-low-risk-level-color)",
            heading: "AssetManagement.value.smartGrading.stepTwo.lowRiskTitle",
            description: "AssetManagement.value.smartGrading.stepTwo.lowRiskDescription",
            count: riskSummary.totalLow,
        },
        {
            variant: "var(--app-medium-risk-level-color)",
            heading: "AssetManagement.value.smartGrading.stepTwo.mediumRiskTitle",
            description: "AssetManagement.value.smartGrading.stepTwo.mediumRiskDescription",
            count: riskSummary.totalMedium,
        },
        {
            variant: "var(--app-high-risk-level-color)",
            heading: "AssetManagement.value.smartGrading.stepTwo.highRiskTitle",
            description: "AssetManagement.value.smartGrading.stepTwo.highRiskDescription",
            count: riskSummary.totalHigh,
        },
    ];

    const getActionDescription = () => {
        if (eligibleAssetsCount === 0) {
            return "";
        }
        return props.allAssetsEligibleForGrading
            ? t("AssetManagement.value.smartGrading.stepTwo.actionDescription_unlocked")
            : t("AssetManagement.value.smartGrading.stepTwo.actionDescription");
    };

    const riskStatusToGradingStatus = () =>
        riskDistributionCardContent.map((item, index) => (
            <Box
                key={index}
                gap={"Sm"}
                className={
                    props.allAssetsEligibleForGrading && index > 0 ? style.allAssetsEligibleForGrading : undefined
                }
            >
                <div className={style.riskVariantIcon} style={{ backgroundColor: item.variant }} />
                <Box columnLayout className={style.flexGrow}>
                    <Box columnLayout>
                        <Box>
                            <Heading variant={"SUBTITLE_2"} disableTopSpacing className={style.flexGrow}>
                                {t(item.heading)}
                            </Heading>
                            <Heading variant={"SUBTITLE_1"} disableBottomSpacing disableTopSpacing>
                                {item.count}
                            </Heading>
                        </Box>
                        <TextBlock className={style.descriptionColor}>{t(item.description)}</TextBlock>
                        {index < riskDistributionCardContent.length - 1 && (
                            <Divider direction={"Horizontal"} className={style.riskChartDivider} />
                        )}
                    </Box>
                </Box>
            </Box>
        ));
    const continueGradingButton = () => (
        <Button
            variant={"Secondary"}
            testId={testIds.workArea.asset.customView.value.continueGradingButton}
            disabled={
                eligibleAssetsCount === 0 ||
                props.isLaunchingGrading ||
                !props.someAssetsEligibleForGrading ||
                !props.isSmartGradingEnabled ||
                !props.isSmartGradingLicenseAvailable
            }
            onClick={props.handleContinueGrading}
        >
            {t("AssetManagement.value.smartGrading.stepTwo.continueGrading")}
        </Button>
    );
    return (
        <>
            <SmartGradingBanner
                isSmartGradingLicenseAvailable={props.isSmartGradingLicenseAvailable}
                currentStepNumber={props.currentlyCompletedStep}
                isSmartGradingEnabled={props.isSmartGradingEnabled}
                eligibleAssetsCount={eligibleAssetsCount}
                onSmartGradingChange={props.handleSmartGradingChange}
                onGradingStepSave={props.handleGradingStepSave}
                isGradingBannerToggleDisabled={props.isGradingBannerToggleDisabled}
            />

            <ValueStepCard
                currentStepNumber={2}
                heading={
                    eligibleAssetsCount === 0
                        ? t("AssetManagement.value.smartGrading.stepTwo.nothingToGradeYetTitle")
                        : props.allAssetsEligibleForGrading
                          ? t("AssetManagement.value.smartGrading.stepTwo.title_unlocked", {
                                count: eligibleAssetsCount,
                            })
                          : t("AssetManagement.value.smartGrading.stepTwo.title", {
                                totalLockedAssets: props.data.lockedAssetsCount,
                            })
                }
                description={
                    eligibleAssetsCount === 0
                        ? t("AssetManagement.value.smartGrading.stepTwo.nothingToGradeYetDescription")
                        : props.allAssetsEligibleForGrading
                          ? t("AssetManagement.value.smartGrading.stepTwo.description_unlocked")
                          : t("AssetManagement.value.smartGrading.stepTwo.description", {
                                totalAssets: calculateTotalAssetsCount(props.data),
                                lockedAssets: props.data.lockedAssetsCount,
                            })
                }
                leftSectionContent={
                    props.data && (
                        <RiskDistributionCard
                            data={riskSummary}
                            hideHeading
                            horizontalDirection
                            lowRiskLabel={t("AssetManagement.value.smartGrading.stepTwo.lowRiskTitle")}
                            mediumRiskLabel={t("AssetManagement.value.smartGrading.stepTwo.mediumRiskTitle")}
                            highRiskLabel={t("AssetManagement.value.smartGrading.stepTwo.highRiskTitle")}
                            customContent={
                                <Box columnLayout horizontalAlignment={"Center"}>
                                    {riskStatusToGradingStatus()}
                                </Box>
                            }
                        />
                    )
                }
                rightSectionContent={
                    <Box columnLayout paddingTop={"Xs"}>
                        <Heading variant={"SUBTITLE_1"} disableBottomSpacing disableTopSpacing>
                            {getActionDescription()}
                        </Heading>
                        <Box paddingTop={"Lg"} gap={"Md"} flexWrap={"Wrap"}>
                            {!props.allAssetsEligibleForGrading && (
                                <Button
                                    testId={testIds.workArea.asset.customView.value.resolveRisksButton}
                                    variant={"Secondary"}
                                    icon={<Event />}
                                    disabled={props.data.lockedAssetsCount <= 0}
                                    onClick={props.handleResolveRisks}
                                >
                                    {t("AssetManagement.value.smartGrading.stepTwo.resolveRisks")}
                                </Button>
                            )}
                            {props.isSmartGradingLicenseAvailable ? (
                                continueGradingButton()
                            ) : (
                                <Tooltip>
                                    <TooltipContent>
                                        {t(
                                            "AssetManagement.value.smartGrading.stepTwo.continueGradingLicenseUnavailable"
                                        )}
                                    </TooltipContent>
                                    <TooltipTrigger>{continueGradingButton()}</TooltipTrigger>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                }
            />
        </>
    );
};
